import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { QueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';

import { useAuthStore } from '@/stores/auth/authStore';
import useStorageService from '@/shared/composables/useStorageService';
import useAuthHelpers from '@/modules/auth/composables/useAuthHelpers';
import useSessionVariable from '@/shared/composables/useSessionVariable';
import { ValuesEnum } from '@/enums/values.enum';

export const authGuard = async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, queryClient: QueryClient) => {
	// Init
	const comesFromPaymentProvider = to.query.fromMollie;
	const pending_id = to.query.pending_id;

	// Check if the user is authenticated
	const authStore = useAuthStore();
	const { isAuthenticated, signInWithSocialLoginQueryParams } = authStore;
	const { user } = storeToRefs(authStore);
	const token = user.value.token;
	const { get } = useStorageService();
	const { logLoginHistory, deleteLoginHistory } = useAuthHelpers();
	const { getSessionVariable, removeSessionVariable } = useSessionVariable();
	const tenantConfig = await get('tenantConfig');
	const publicMode = tenantConfig?.enable_public_mode ?? null;
	const fullGuestMode = tenantConfig?.enable_full_guest_mode ?? null;
	const orderId = to.query.orderId;
	const voucherID = Array.isArray(to.query.voucherID) ? to.query.voucherID[0] : to.query.voucherID;

	const loginMethod = to.query.loginMethod;
	if (loginMethod && loginMethod !== 'undefined' && loginMethod !== 'null' && loginMethod !== '') {
		await logLoginHistory(loginMethod.toString());
	}

	// If comes from payment provider
	if (comesFromPaymentProvider == 'true' || comesFromPaymentProvider == '1') {
		// This will catch the case where user is on web + not main app domain
		if (tenantConfig?.main_domain !== window.location.hostname && window.location.hostname !== 'localhost') {
			next({ path: `/transaction-confirmation/${orderId}` });
		} else {
			if (orderId) {
				next({
					path: '/transaction',
					query: {
						orderId,
						pending_id,
					},
				});
			} else {
				next({ path: '/transaction' });
			}
		}
	}
	// Check for Payconiq payment return when query string was stripped by bank app
	else if (await getSessionVariable(ValuesEnum.SessionPayconiqPaymentInProgress)) {
		// Clear the session flag
		removeSessionVariable(ValuesEnum.SessionPayconiqPaymentInProgress);

		// Redirect to dashboard (since we can't detect orderId without query params)
		next('/dashboard');
	}
	// If access token exists on the url
	else if (to.query.accessToken) {
		// If accessToken is in params, try to login with it
		const signedIn = await signInWithSocialLoginQueryParams(to);

		// If accessToken param or token exist redirect to dashboard
		if (signedIn || token) {
			next('/dashboard');
		} else {
			if (!comesFromPaymentProvider) {
				await deleteLoginHistory(loginMethod?.toString() || '');
			}
			// Otherwise continue navigation
			next();
		}
	} else {
		if (publicMode === null || fullGuestMode === null) {
			next();
		} else if (to.name === 'dashboard' && to.query.spotid) {
			next('/self-ordering/get-spot-id?spotid=' + to.query.spotid);
		} else if (publicMode === false && fullGuestMode === false) {
			// If accessToken is in params, try to login with it
			const signedIn = (await signInWithSocialLoginQueryParams(to)) || isAuthenticated();

			// If is not logged in and going to auth
			if (!signedIn && !token && (to.fullPath.includes('auth') || to.fullPath.includes('magic-login'))) {
				// Let it continue
				next();
			}
			// If is not logged in and going to public routes
			else if (
				(!signedIn &&
					!token &&
					(to.name === 'activate' ||
						to.name === 'top-up' ||
						to.name === 'transaction-added' ||
						to.name === 'wallet-guest' ||
						to.name === 'wallet-history' ||
						to.name === 'wallet-guest-link-bracelet' ||
						to.name === 'wallet-voucher-refund' ||
						to.name === 'profile-password-reset-request' ||
						to.name === 'profile-refunds-guest-request' ||
						to.name === 'guest-wallet' ||
						to.name === 'rfid-guest-topup' ||
						to.name === 'share' ||
						to.name === 'self-ordering-get-spot-id' ||
						to.name === 'self-ordering' ||
						to.name === 'self-ordering-cart' ||
						to.name === 'checkout' ||
						to.name === 'transaction-confirmation' ||
						to.name === 'receipt')) ||
				to.name === 'top-up-overview'
			) {
				// Let it continue
				next();
			}
			// If is not logged in, send to auth path
			else if (!signedIn && !token) {
				if (voucherID) {
					next({
						path: '/auth/signuphome',
						query: {
							callbackPage: 'redeem-voucher',
							id: voucherID,
						},
					});
				} else {
					next('/auth/signuphome');
				}
			}
			// If logged in, go to dashboard
			else if ((signedIn && to.name === 'sign-in-home') || to.name === 'sign-up-with-email') {
				next('/dashboard');
			} else {
				next();
			}
		}
		// If public mode or full guest mode, let it continue
		else {
			next();
		}
	}
};
